/**
 * Memoized selectors for workspace state
 *
 * These selectors use reselect (built into Redux Toolkit) to prevent unnecessary re-renders.
 * They only recompute when their input selectors change.
 */

import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from '../index';
import type { ReadingWithData, CanvasWithData, PaperWithData } from '../../types/workspace';
import type { Category } from '../../types/models';
import {
  evaluateArgument,
  type CheckableRectangle,
  type ConditionId,
  type NodeDiagnostic,
} from '@features/annogram/canvas/utils/argumentChecks';
import { DEFAULT_RUBRIC_SETTINGS } from '../../shared/utils/defaultRubricSettings';
import { assessHighlights } from '@features/annogram/sidebar/utils/assessmentChecks';
import {
  assessCanvas,
  type SourceBridge,
} from '@features/annogram/canvas/utils/assessCanvas';
import type { IHighlight } from '@argument-studio/react-pdf-highlighter-with-categories';

const EMPTY_CATEGORIES: Category[] = [];

// ============================================================================
// Basic workspace selectors
// ============================================================================

export const selectWorkspaceId = (state: RootState) => state.workspace.workspace_id;
export const selectWorkspaceName = (state: RootState) => state.workspace.workspace_name;
export const selectSharedData = (state: RootState) => state.workspace.shared_data;
export const selectUserData = (state: RootState) => state.workspace.user_data;
export const selectCategories = (state: RootState) =>
  state.workspace.shared_data?.categories ?? EMPTY_CATEGORIES;

// ============================================================================
// Selection state selectors
// ============================================================================

export const selectSelectedReadingId = (state: RootState) => state.workspace.selectedReadingId;
export const selectSelectedCanvasId = (state: RootState) => state.workspace.selectedCanvasId;
export const selectSelectedPaperId = (state: RootState) => state.workspace.selectedPaperId;

// ============================================================================
// Dirty tracking selectors
// ============================================================================

export const selectDirtyResources = (state: RootState) => state.workspace.dirtyResources || { reading: false, canvas: false, paper: false };
export const selectIsReadingDirty = (state: RootState) => state.workspace.dirtyResources?.reading || false;
export const selectIsCanvasDirty = (state: RootState) => state.workspace.dirtyResources?.canvas || false;
export const selectIsPaperDirty = (state: RootState) => state.workspace.dirtyResources?.paper || false;
export const selectIsAnyResourceDirty = (state: RootState) =>
  state.workspace.dirtyResources?.reading ||
  state.workspace.dirtyResources?.canvas ||
  state.workspace.dirtyResources?.paper ||
  false;

// ============================================================================
// Resource list selectors
// ============================================================================

export const selectReadings = (state: RootState) => state.workspace.readings;
export const selectCanvases = (state: RootState) => state.workspace.canvases;
export const selectPapers = (state: RootState) => state.workspace.papers;

// ============================================================================
// Memoized current resource selectors
// ============================================================================

/**
 * Get the currently selected reading with parsed data.
 * Memoized - only recomputes when readings array or selectedReadingId changes.
 */
export const selectCurrentReading = createSelector(
  [selectReadings, selectSelectedReadingId],
  (readings, selectedId): ReadingWithData | null => {
    if (!selectedId) return null;
    return readings.find(r => r.reading_id === selectedId) || null;
  }
);

/**
 * Get the currently selected canvas with parsed data.
 * Memoized - only recomputes when canvases array or selectedCanvasId changes.
 */
export const selectCurrentCanvas = createSelector(
  [selectCanvases, selectSelectedCanvasId],
  (canvases, selectedId): CanvasWithData | null => {
    if (!selectedId) return null;
    return canvases.find(c => c.canvas_id === selectedId) || null;
  }
);

/**
 * Get the currently selected paper with parsed data.
 * Memoized - only recomputes when papers array or selectedPaperId changes.
 */
export const selectCurrentPaper = createSelector(
  [selectPapers, selectSelectedPaperId],
  (papers, selectedId): PaperWithData | null => {
    if (!selectedId) return null;
    return papers.find(p => p.paper_id === selectedId) || null;
  }
);

// ============================================================================
// Current resource data selectors (deeply memoized)
// ============================================================================

/**
 * Get the current user's own highlights on the current reading. Used by
 * mutation thunks (they always target the requester's own bucket) and
 * by RubricSection's "Save as expert reference" button (it snapshots
 * the owner's own work).
 *
 * Display surfaces (PdfViewer, sidebar list, Canvas references, grade
 * computation) should use `selectViewedHighlights` instead so the
 * Stage 3.7 grading view can transparently swap in a contributor's
 * highlights.
 */
export const selectCurrentHighlights = createSelector(
  [selectCurrentReading],
  (reading) => reading?.reading_data?.highlights || []
);

/**
 * The highlights currently being displayed for the active reading.
 * When the workspace owner has entered a grading view (Stage 3.7),
 * this returns the selected contributor's highlights; otherwise it
 * mirrors `selectCurrentHighlights` (the requester's own).
 *
 * Wiring this selector — instead of `selectCurrentHighlights` — into
 * the PDF reader, sidebar list, canvas references, and grade
 * computation is what makes the grading view actually swap out the
 * displayed work.
 */
export const selectViewedHighlights = createSelector(
  [selectCurrentReading, (state: RootState) => state.workspace.gradingView],
  (reading, gradingView) =>
    gradingView
      ? reading?.gradingView?.highlights ?? []
      : reading?.reading_data?.highlights ?? []
);

/**
 * annotation_client_id → PDF page number, over the currently viewed
 * highlights. Backfills the page badge for rects created before
 * source_page_number existed: their stored provenance id resolves to a
 * live highlight whose page we can still read. Rects whose source
 * annotation is gone simply have no derived page — the stored field on
 * the rect (when present) always wins.
 */
export const selectPageByAnnotationId = createSelector(
  [selectViewedHighlights],
  (highlights): Record<string, number> =>
    Object.fromEntries(
      highlights.map((h) => [h.id, h.position.pageNumber])
    )
);

/**
 * Stage 6.8: picker selection lives at workspace level so it carries
 * across the reader/canvas toggle. Per-resource gradingView slots are
 * just cached loaded data (annotations / canvas rectangles).
 */
export const selectIsGradingViewActive = (state: RootState) =>
  state.workspace.gradingView !== undefined;

export const selectGradingViewUserId = (state: RootState) =>
  state.workspace.gradingView?.userId ?? null;

export const selectGradingViewUsername = (state: RootState) =>
  state.workspace.gradingView?.username ?? null;

const DEFAULT_GRADE_WEIGHTS = Object.freeze({ reader: 33, canvas: 33, editor: 34 });
export const selectGradeWeights = (state: RootState) =>
  state.workspace.gradeWeights ?? DEFAULT_GRADE_WEIGHTS;

const EMPTY_EDITOR_CRITERIA: string[] = [];
export const selectEditorRubricCriteria = (state: RootState) =>
  state.workspace.editorRubricCriteria ?? EMPTY_EDITOR_CRITERIA;

const EMPTY_DIM_WEIGHTS = Object.freeze({});
export const selectGradeDimensionWeights = (state: RootState) =>
  state.workspace.gradeDimensionWeights ?? (EMPTY_DIM_WEIGHTS as NonNullable<RootState["workspace"]["gradeDimensionWeights"]>);

const EMPTY_CONTRIBUTORS: never[] = [];
/** Cached contributor list for the current reading. */
export const selectCurrentReadingContributors = createSelector(
  [selectCurrentReading],
  (reading) => reading?.contributorsCache?.contributors ?? (EMPTY_CONTRIBUTORS as never[])
);

/**
 * Whether the current user is the workspace owner. Derived from
 * workspace_permissions and state.user.userId — no backend roundtrip.
 * Gates owner-only UI (e.g., the grading view picker).
 */
export const selectIsCurrentUserWorkspaceOwner = createSelector(
  [
    (state: RootState) => state.workspace.workspace_permissions,
    (state: RootState) => state.user.userId,
  ],
  (permissions, userId) => {
    if (!userId || !permissions) return false;
    return permissions.some(
      (p) => p.user_id === userId && p.permission_name === 'owner'
    );
  }
);

/**
 * Access-control tier selectors (Phase 1). Populated by /users/me on
 * login; falls back to the safest read ('annotator') so UI gates
 * fail closed when the value hasn't loaded yet.
 *
 * selectIsEvaluator returns true for both 'evaluator' and the
 * higher 'platform_admin' tier — mirrors RequireTier's
 * minimum-threshold semantics so the UI never disagrees with what
 * the backend will allow.
 */
const TIER_RANK = {
  annotator: 0,
  practitioner: 1,
  evaluator: 2,
  platform_admin: 3,
} as const;

const tierRank = (t: string): number =>
  (TIER_RANK as Record<string, number | undefined>)[t] ?? -1;

export const selectCurrentUserTier = (state: RootState): string =>
  state.auth.userInfo?.tier ?? 'annotator';

export const selectCurrentUserTierSource = (state: RootState): string =>
  state.auth.userInfo?.tier_source ?? 'default';

export const selectIsEvaluator = (state: RootState): boolean =>
  tierRank(selectCurrentUserTier(state)) >= TIER_RANK.evaluator;

export const selectIsPlatformAdmin = (state: RootState): boolean =>
  tierRank(selectCurrentUserTier(state)) >= TIER_RANK.platform_admin;

/**
 * Get PDF URL from the current reading.
 * Memoized - only recomputes when current reading changes.
 */
export const selectCurrentPdfUrl = createSelector(
  [selectCurrentReading],
  (reading) => reading?.document_url || ''
);

// Removed: selectCurrentPdfData - PDF data is now cached in IndexedDB instead of Redux

/**
 * Get rectangles from the current canvas.
 * Memoized - only recomputes when current canvas changes.
 */
export const selectCurrentRectangles = createSelector(
  [selectCurrentCanvas],
  (canvas) => canvas?.canvas_data?.rectangles || []
);

/**
 * Get scale from the current canvas.
 * Memoized - only recomputes when current canvas changes.
 */
export const selectCurrentCanvasScale = createSelector(
  [selectCurrentCanvas],
  (canvas) => canvas?.canvas_data?.scale || 1
);

/**
 * Whether Parsons evaluation mode is enabled on the current canvas.
 * Defaults to false for canvases that haven't opted in.
 */
export const selectCurrentEvaluationMode = createSelector(
  [selectCurrentCanvas],
  (canvas) => canvas?.canvas_data?.evaluation_mode ?? false
);

/**
 * Stage 6.8: canvas display selectors parallel selectViewedHighlights.
 * When grading view is active, return the contributor's canvas data
 * from the per-canvas grading view cache; otherwise the owner's own.
 */
export const selectViewedCanvasData = createSelector(
  [selectCurrentCanvas, (state: RootState) => state.workspace.gradingView],
  (canvas, gradingView) =>
    gradingView
      ? canvas?.gradingView?.canvasData
      : canvas?.canvas_data
);

export const selectViewedRectangles = createSelector(
  [selectViewedCanvasData],
  (data) => data?.rectangles || []
);

export const selectViewedCanvasScale = createSelector(
  [selectViewedCanvasData],
  (data) => data?.scale || 1
);

export const selectViewedEvaluationMode = createSelector(
  [
    selectViewedCanvasData,
    (state: RootState) => state.workspace.gradingView,
    (state: RootState) => state.workspace.gradingViewEvaluation,
  ],
  (data, gradingView, overlayFlag) =>
    gradingView ? overlayFlag ?? false : data?.evaluation_mode ?? false
);

const EMPTY_CANVAS_CONTRIBUTORS: never[] = [];
export const selectCurrentCanvasContributors = createSelector(
  [selectCurrentCanvas],
  (canvas) => canvas?.contributorsCache?.contributors ?? (EMPTY_CANVAS_CONTRIBUTORS as never[])
);

/**
 * Groups on the currently-viewed canvas. Uses the same view-aware data
 * source as selectViewedRectangles so the grading-view path surfaces
 * the contributor's groups (not the owner's own). Returns a stable
 * empty array on cache miss so consumers' useMemo / equality checks
 * don't thrash for pre-grouping canvases.
 */
const EMPTY_GROUPS: never[] = [];
export const selectViewedGroups = createSelector(
  [selectViewedCanvasData],
  (data) => data?.groups ?? (EMPTY_GROUPS as never[])
);

const EMPTY_GROUP_EDGES: never[] = [];
export const selectViewedGroupEdges = createSelector(
  [selectViewedCanvasData],
  (data) => data?.groupEdges ?? (EMPTY_GROUP_EDGES as never[])
);

/**
 * Role labels resolved from shared_data with defaults. Used by the
 * argument-checks utility to know which categories play the setting,
 * target, and marginalia roles.
 */
export const selectRoleLabels = createSelector(
  [selectSharedData],
  (sharedData) => ({
    setting: sharedData?.setting_category_label || "Setting",
    target: sharedData?.target_category_label || "Target",
    marginalia: sharedData?.marginalia_category_label || "Question",
  })
);

/**
 * Whether at least one rectangle is tagged with the setting role.
 * Used by the canvas toolbar to show a "no setting nodes" hint
 * when evaluation mode is on but the [1] check can't fire usefully.
 */
export const selectHasSettingNode = createSelector(
  [selectViewedRectangles, selectRoleLabels],
  (rectangles, roles) =>
    rectangles.some((r) => r.category === roles.setting)
);

/**
 * Whether a canvas rectangle is currently being dragged. Used by the
 * argument-evaluation selector to skip recomputation during drag frames,
 * since rectangle position has no effect on the dependency graph and
 * the recompute (O(V·(V+E))) on every cursor frame caused visible lag.
 */
// True during any canvas-level drag that translates rectangles —
// either a single-rect drag (draggedRectIdx) or a group-frame drag
// (draggedGroupId). Argument evaluation is cached during the drag
// because positions don't affect any condition; a fresh evaluation
// runs as soon as the drag ends.
const selectIsDraggingRect = (state: RootState) =>
  state.canvasAux.draggedRectIdx !== null ||
  state.canvasAux.draggedGroupId !== null;

// Module-level cache of the last evaluation result. Held across drag-move
// frames so dragging a rect doesn't trigger a recompute on every dispatch.
// Cleared when evaluation mode is turned off.
let _cachedEvaluation: ReturnType<typeof evaluateArgument> | null = null;

/**
 * Builds a scope view of the canvas: a sub-array of rectangles whose
 * arrows have been filtered to intra-scope edges and re-indexed into
 * the sub-array. Returns the sub-array plus a localIdx→globalIdx
 * translation so callers can map diagnostics back to canvas-level
 * rect indices.
 *
 * Phase 2 design call: cross-group arrows are visual annotations
 * only. They do NOT contribute to either group's evaluation. The
 * original plan toyed with "external incoming acts as a premise" but
 * the implementation cost and user-facing ambiguity wasn't worth it —
 * if the user wants the dependency to count, they ungroup or merge.
 */
function buildScopeView(
  rectangles: ReadonlyArray<CheckableRectangle>,
  scopeIndices: ReadonlyArray<number>
): { rects: CheckableRectangle[]; localToGlobal: number[] } {
  // Sort so the scope's index order matches canvas order — produces
  // stable diagnostics output across runs.
  const localToGlobal = [...scopeIndices].sort((a, b) => a - b);
  const globalToLocal = new Map<number, number>();
  localToGlobal.forEach((g, l) => globalToLocal.set(g, l));

  const rects: CheckableRectangle[] = localToGlobal.map((globalIdx) => {
    const rect = rectangles[globalIdx];
    if (!rect) return { category: undefined, arrows: [] };
    const arrows = rect.arrows
      .filter((a) => globalToLocal.has(a.endRectIdx))
      .map((a) => ({
        ...a,
        endRectIdx: globalToLocal.get(a.endRectIdx) as number,
      }));
    return { ...rect, arrows };
  });

  return { rects, localToGlobal };
}

/**
 * Argument-evaluation result for the current canvas. Returns null when
 * evaluation mode is off so the UI can skip rendering tints/tooltips.
 * During an in-progress rectangle drag, returns the prior cached result
 * (positions don't affect any condition) — a fresh evaluation runs as
 * soon as the drag ends.
 *
 * Scoping (Phase 2):
 *   - Each group with evaluate=true gets its own evaluation pass on
 *     its member rects, tagged with the groupId so the UI can anchor
 *     chips to the right frame.
 *   - Rects in NO group get the canvas-wide pass (ungrouped scope).
 *   - Rects in groups whose evaluate=false are skipped entirely — the
 *     user opted them out of evaluation, so no diagnostics for them.
 *   - Cross-group arrows are ignored (see buildScopeView).
 */
export const selectArgumentEvaluation = createSelector(
  [
    selectViewedRectangles,
    selectViewedGroups,
    selectRoleLabels,
    selectViewedEvaluationMode,
    selectIsDraggingRect,
  ],
  (rectangles, groups, roles, evaluationMode, isDragging) => {
    if (!evaluationMode) {
      _cachedEvaluation = null;
      return null;
    }
    if (isDragging) {
      return _cachedEvaluation;
    }

    // Map each rect index to its group (if any). Used to compute the
    // ungrouped scope and to skip rects in unevaluated groups.
    const indexToGroupId = new Map<number, string>();
    const evaluatedGroupIds = new Set<string>();
    for (const g of groups) {
      for (const i of g.memberRectIndices) indexToGroupId.set(i, g.id);
      if (g.evaluate) evaluatedGroupIds.add(g.id);
    }

    type Scope = { indices: number[]; groupId?: string };
    const scopes: Scope[] = [];

    // Each evaluated group becomes its own scope.
    for (const g of groups) {
      if (!g.evaluate) continue;
      if (g.memberRectIndices.length === 0) continue;
      scopes.push({ indices: g.memberRectIndices, groupId: g.id });
    }

    // Ungrouped rects (no group at all) get the canvas-wide pass.
    // Rects in unevaluated groups are deliberately omitted.
    const ungrouped: number[] = [];
    for (let i = 0; i < rectangles.length; i++) {
      if (!indexToGroupId.has(i)) ungrouped.push(i);
    }
    if (ungrouped.length > 0) {
      scopes.push({ indices: ungrouped });
    }

    const allDiagnostics: NodeDiagnostic[] = [];
    const byRect = new Map<number, NodeDiagnostic[]>();
    const violatedConditions = new Set<ConditionId>();

    for (const scope of scopes) {
      const { rects, localToGlobal } = buildScopeView(
        rectangles,
        scope.indices
      );
      const result = evaluateArgument(rects, roles);
      for (const d of result.diagnostics) {
        const globalIdx = localToGlobal[d.rectIdx];
        if (globalIdx === undefined) continue;
        const globalD: NodeDiagnostic = {
          ...d,
          rectIdx: globalIdx,
          ...(scope.groupId ? { groupId: scope.groupId } : {}),
        };
        allDiagnostics.push(globalD);
        const list = byRect.get(globalIdx);
        if (list) list.push(globalD);
        else byRect.set(globalIdx, [globalD]);
        violatedConditions.add(globalD.condition);
      }
    }

    _cachedEvaluation = {
      diagnostics: allDiagnostics,
      byRect,
      violatedConditions,
    };
    // Mark this read so the linter / future-me sees the side-channel
    // value isn't accidentally unused.
    void evaluatedGroupIds;
    return _cachedEvaluation;
  }
);

/**
 * Per-group diagnostic summary, keyed by groupId. Only groups with
 * evaluate=true appear in the map — non-evaluating groups produce no
 * entry, so consumers can distinguish "evaluated and clean" (entry
 * with count=0) from "not evaluated" (no entry). hasCycle mirrors
 * the toolbar's coloring rule: any cycle (condition 4) or petitio
 * pushes the chip into red; otherwise it's the warning color.
 */
export const selectGroupEvaluationSummary = createSelector(
  [selectArgumentEvaluation, selectViewedGroups],
  (evaluation, groups) => {
    const summary = new Map<string, { count: number; hasCycle: boolean }>();
    for (const g of groups) {
      if (g.evaluate) summary.set(g.id, { count: 0, hasCycle: false });
    }
    if (!evaluation) return summary;
    for (const d of evaluation.diagnostics) {
      if (!d.groupId) continue;
      const entry = summary.get(d.groupId);
      if (!entry) continue;
      entry.count++;
      if (d.condition === "4" || d.fallacy === "petitio_principii") {
        entry.hasCycle = true;
      }
    }
    return summary;
  }
);

/**
 * Rubric settings for the currently selected reading. Falls back to the
 * shared DEFAULT_RUBRIC_SETTINGS constant when no reading is selected or
 * the reading has no rubric yet — the constant is a stable module-level
 * reference so consumers don't trigger reference-equality warnings.
 */
export const selectCurrentRubricSettings = createSelector(
  [selectCurrentReading],
  (reading) => reading?.rubric?.settings ?? DEFAULT_RUBRIC_SETTINGS
);

const EMPTY_EXPERT_HIGHLIGHTS: never[] = [];

/**
 * Expert reference highlights for the currently selected reading.
 * Stable empty-array fallback to avoid reference-equality warnings.
 */
export const selectCurrentExpertHighlights = createSelector(
  [selectCurrentReading],
  (reading) =>
    reading?.rubric?.expertHighlights ?? (EMPTY_EXPERT_HIGHLIGHTS as never[])
);

/**
 * Increments each time loadRubric hydrates the current reading's rubric
 * from the backend. Used by RubricSection's auto-save effect to skip
 * the spurious save that would otherwise follow a hydration.
 */
export const selectCurrentRubricLoadGen = createSelector(
  [selectCurrentReading],
  (reading) => reading?.rubric?.loadGen ?? 0
);

/**
 * Stage 5: timestamp of the contributor's last successful assessment
 * auto-save for the current reading. Null when nothing has been saved
 * yet this session. GradeSection turns this into a "saved Xs ago"
 * indicator.
 */
export const selectCurrentAssessmentLastSavedAt = createSelector(
  [selectCurrentReading],
  (reading) => reading?.assessmentMeta?.lastSavedAt ?? null
);

/**
 * Stage 6.2: the workspace owner's saved expert-canvas snapshot for
 * the currently selected canvas (or null if no rubric exists yet).
 * Drives the canvas-grading util's comparison.
 */
export const selectCurrentCanvasExpertSnapshot = createSelector(
  [selectCurrentCanvas],
  (canvas) => canvas?.rubric?.expertCanvas ?? null
);

/**
 * Stage 6.6: timestamp of the contributor's last successful canvas
 * assessment auto-save. Null when none yet this session.
 */
export const selectCurrentCanvasAssessmentLastSavedAt = createSelector(
  [selectCurrentCanvas],
  (canvas) => canvas?.assessmentMeta?.lastSavedAt ?? null
);

// ============================================================================
// Reading grade (Item 7, Stage 3 → 3.8)
//
// Stage 3.8 dropped the standalone overlap-precision dimension. Two
// scored dimensions remain (completeness, category accuracy) and one
// informational metric (averageMatchedIoU). Match-quality is folded
// into completeness via graduated credit — see
// assessmentChecks.assessHighlights.
// ============================================================================

interface ReadingGradeDimension {
  enabled: boolean;
  /** 0-100 when the dimension is enabled and an expert reference exists; null otherwise. */
  score: number | null;
  /** Count of items that contributed to deductions. */
  violations: number;
}

export interface ReadingGrade {
  completeness: ReadingGradeDimension;
  categoryAccuracy: ReadingGradeDimension;
  /** Average of enabled dimension scores, rounded. Null when no enabled dimensions or no expert ref. */
  total: number | null;
  hasExpertReference: boolean;
  /**
   * Total number of highlights in the expert reference set — denominator
   * for "X/N matched" displays. Zero when no expert reference is set.
   */
  expertCount: number;
  /** Number of expert highlights the student matched at or above threshold. */
  matchedCount: number;
  /**
   * Number of expert highlights the student partially matched
   * (0 < credit < 1, i.e. some overlap but below threshold).
   */
  partialCount: number;
  /** Number of expert highlights with zero overlap. */
  fullyMissedCount: number;
  /**
   * Fractional missed count (Stage 3.8): each expert highlight contributes
   * `1 - credit` to the missed pool, so a near-miss is partially missed.
   * Drives the completeness deduction.
   */
  missedCount: number;
  /** Among matched pairs, how many disagree on category. */
  miscategorizedCount: number;
  /**
   * Coverage quality (Stage 3.8 follow-up): mean completion credit across
   * ALL expert highlights, in [0, 1]. Stage 3.8 originally surfaced the
   * mean IoU among matched pairs, but that fake-highs on sparse matches
   * (1/3 matched at 97% IoU still reads as "97%"). Coverage uses the
   * same `credit = min(1, IoU/threshold)` per expert that drives the
   * completeness deduction, so it's a sample-size-aware summary of how
   * much of the expert set the student covered.
   */
  coverageQuality: number;
  /** Expert highlights with credit < 1 (partially or fully missed). Drives the "Missed" list. */
  missedHighlights: IHighlight[];
}

const EMPTY_GRADE: ReadingGrade = Object.freeze({
  completeness: { enabled: true, score: null, violations: 0 },
  categoryAccuracy: { enabled: true, score: null, violations: 0 },
  total: null,
  hasExpertReference: false,
  expertCount: 0,
  matchedCount: 0,
  partialCount: 0,
  fullyMissedCount: 0,
  missedCount: 0,
  miscategorizedCount: 0,
  coverageQuality: 0,
  missedHighlights: [],
}) as ReadingGrade;

/**
 * Computed grade for the currently selected reading. Runs the
 * assessment utility against the viewed highlights (Stage 3.7 — the
 * contributor's when grading, else the user's own) and the rubric's
 * expert reference set.
 *
 * Scoring model (Stage 3.8):
 *   completenessScore = max(0, 100 − completeness.penalty × missedCount)
 *     where missedCount is fractional — a 70% IoU at threshold 75 counts
 *     as ~0.07 missed, not "1 missed"
 *   categoryScore = max(0, 100 − categoryMismatch.penalty × miscategorizedCount)
 *     mismatches only count within matched (≥ threshold) pairs
 *   total = average of enabled dimensions, rounded
 *
 * averageMatchedIoU is informational, not in the total.
 */
export const selectReadingGrade = createSelector(
  [
    selectViewedHighlights,
    selectCurrentExpertHighlights,
    selectCurrentRubricSettings,
  ],
  (student, expert, settings): ReadingGrade => {
    if (expert.length === 0) {
      return {
        ...EMPTY_GRADE,
        completeness: { enabled: settings.completeness.enabled, score: null, violations: 0 },
        categoryAccuracy: { enabled: settings.categoryMismatch.enabled, score: null, violations: 0 },
      };
    }

    // settings.matchThreshold is 0-100 (percent); assessHighlights wants
    // a 0-1 fraction.
    const thresholdFraction = settings.matchThreshold / 100;
    const assessment = assessHighlights(student, expert, thresholdFraction);

    // Fractional missed: each expert highlight contributes `1 - credit`,
    // so partial credit reduces the deduction proportionally. Rounded
    // for display in the violations count.
    const fractionalMissed = expert.length - assessment.totalCredit;

    const completenessScore = settings.completeness.enabled
      ? Math.max(
          0,
          100 - settings.completeness.penalty * fractionalMissed
        )
      : null;

    const categoryAccuracyScore = settings.categoryMismatch.enabled
      ? Math.max(
          0,
          100 - settings.categoryMismatch.penalty * assessment.miscategorizedCount
        )
      : null;

    const enabledScores = [completenessScore, categoryAccuracyScore].filter(
      (s): s is number => s !== null
    );
    const total = enabledScores.length > 0
      ? Math.round(enabledScores.reduce((a, b) => a + b, 0) / enabledScores.length)
      : null;

    // The "missed" list (for the GradeSection breakdown) is the experts
    // that fell below threshold — what the student didn't really cover.
    const missedHighlights = assessment.outcomes
      .filter((o) => o.credit < 1)
      .map((o) => o.expert as IHighlight);

    // Coverage = mean credit across all experts. Naturally in [0, 1].
    const coverageQuality = assessment.totalCredit / expert.length;
    // Bucket sizes for the breakdown display: fully matched (credit==1),
    // partially matched (0 < credit < 1), fully missed (credit==0).
    const partialCount = assessment.outcomes.filter(
      (o) => o.credit > 0 && o.credit < 1
    ).length;
    const fullyMissedCount = assessment.outcomes.filter(
      (o) => o.credit === 0
    ).length;

    return {
      completeness: {
        enabled: settings.completeness.enabled,
        score:
          completenessScore !== null ? Math.round(completenessScore) : null,
        violations: assessment.missedCount,
      },
      categoryAccuracy: {
        enabled: settings.categoryMismatch.enabled,
        score:
          categoryAccuracyScore !== null
            ? Math.round(categoryAccuracyScore)
            : null,
        violations: assessment.miscategorizedCount,
      },
      total,
      hasExpertReference: true,
      expertCount: expert.length,
      matchedCount: assessment.matchedCount,
      partialCount,
      fullyMissedCount,
      missedCount: fractionalMissed,
      miscategorizedCount: assessment.miscategorizedCount,
      coverageQuality,
      missedHighlights,
    };
  }
);

// ============================================================================
// Visual diagnostics (Item 7, Stage 4)
//
// Powers the PDF reader's tint/ghost overlay: for each viewed (student)
// highlight, what's its assessment status against the expert reference?
// And which expert highlights weren't covered (renderable as ghost
// outlines)?
// ============================================================================

export type HighlightDiagnostic =
  | "matched"
  | "category_mismatch"
  | "low_overlap"
  | "extra";

export interface AssessmentDiagnostics {
  /** Per-student-highlight status keyed by highlight id. */
  statusById: Record<string, HighlightDiagnostic>;
  /**
   * Per-highlight diagnostic bullets, surfaced by DiagnosticBadge on
   * hover. One string per bullet so the tooltip can render them as a
   * list rather than a run-on sentence. Absent for matched highlights.
   */
  messagesById: Record<string, string[]>;
  /**
   * Expert highlights the student didn't fully match (credit < 1) —
   * rendered as dashed/translucent ghosts over the PDF so the grader
   * (and the contributor viewing their own grade) can see what was
   * missed in situ.
   */
  ghosts: IHighlight[];
  /** Convenience flag — true when there's an expert reference set. */
  active: boolean;
}

const EMPTY_DIAGNOSTICS: AssessmentDiagnostics = Object.freeze({
  statusById: Object.freeze({}) as Record<string, HighlightDiagnostic>,
  messagesById: Object.freeze({}) as Record<string, string[]>,
  ghosts: [],
  active: false,
}) as AssessmentDiagnostics;

/**
 * Computes per-highlight diagnostic status from the same assessment
 * the grade selector uses. Derived directly from the assessment's
 * per-expert outcomes:
 *   credit==1, categories agree → matched
 *   credit==1, categories disagree → category_mismatch
 *   0 < credit < 1                → low_overlap
 *   student unmatched (no expert) → extra
 *
 * Ghosts = experts with credit < 1 (partial OR fully missed). The
 * renderer treats them as outlines, not solid highlights, and skips
 * the popup wrapping so they're visual-only.
 */
export const selectAssessmentDiagnostics = createSelector(
  [
    selectViewedHighlights,
    selectCurrentExpertHighlights,
    selectCurrentRubricSettings,
  ],
  (student, expert, settings): AssessmentDiagnostics => {
    if (expert.length === 0) return EMPTY_DIAGNOSTICS;

    const thresholdFraction = settings.matchThreshold / 100;
    const assessment = assessHighlights(student, expert, thresholdFraction);

    const statusById: Record<string, HighlightDiagnostic> = {};
    const messagesById: Record<string, string[]> = {};
    const claimedStudentIds = new Set<string>();
    const thresholdPct = Math.round(settings.matchThreshold);

    for (const outcome of assessment.outcomes) {
      if (!outcome.bestMatch) continue;
      const sId = outcome.bestMatch.student.id;
      claimedStudentIds.add(sId);
      if (outcome.credit === 1) {
        if (outcome.categoriesAgree) {
          statusById[sId] = "matched";
        } else {
          statusById[sId] = "category_mismatch";
          const expertCat = (outcome.expert as IHighlight).comment?.category;
          const studentCat = outcome.bestMatch.student.comment?.category;
          messagesById[sId] = [
            "Right span, wrong category.",
            `Expected: ${expertCat ? `"${expertCat}"` : "(uncategorized)"}`,
            `Got: ${studentCat ? `"${studentCat}"` : "(uncategorized)"}`,
          ];
        }
      } else {
        statusById[sId] = "low_overlap";
        const iouPct = Math.round(outcome.bestMatch.iou * 100);
        messagesById[sId] = [
          `Overlap ${iouPct}% with the expert highlight.`,
          `Below the ${thresholdPct}% match threshold.`,
        ];
      }
    }
    // Student highlights that didn't pair with any expert → extras.
    for (const s of student) {
      if (!claimedStudentIds.has(s.id)) {
        statusById[s.id] = "extra";
        messagesById[s.id] = [
          "No matching expert highlight.",
          "Informational — may be legitimate beyond-rubric work.",
        ];
      }
    }

    // Ghosts: experts with any credit < 1 (partial or fully missed).
    const ghosts = assessment.outcomes
      .filter((o) => o.credit < 1)
      .map((o) => o.expert as IHighlight);

    return { statusById, messagesById, ghosts, active: true };
  }
);

// ============================================================================
// Canvas grade (Item 7, Stage 6.4)
//
// Mirrors selectReadingGrade. Dimensions specific to the canvas:
//   - Node coverage: did the student create rects for the expert's rects?
//     Graduated by source-ID correspondence (no IoU concept — match
//     is binary on provenance).
//   - Node category accuracy: among matched nodes, do categories agree?
//   - Edge coverage: did the student wire up corresponding rects the
//     way the expert did?
// Plus informational:
//   - Parsons parity (expert violations vs student violations)
//   - Duplicate/orphan counts
// ============================================================================

interface CanvasGradeDimension {
  enabled: boolean;
  score: number | null;
  violations: number;
}

export interface CanvasGrade {
  nodeCoverage: CanvasGradeDimension;
  categoryAccuracy: CanvasGradeDimension;
  edgeCoverage: CanvasGradeDimension;
  total: number | null;
  hasExpertReference: boolean;

  // Counts useful for the UI breakdown.
  expertNodeCount: number;
  matchedNodeCount: number;
  missingNodeCount: number;
  miscategorizedCount: number;
  expertEdgeCount: number;
  matchedEdgeCount: number;
  missingEdgeCount: number;
  extraEdgeCount: number;
  duplicateStudentCount: number;
  orphanStudentCount: number;
  orphanExpertCount: number;

  // Parsons-condition parity, layered on by evaluating both canvases
  // with the same role labels.
  parsons: {
    expertViolationCount: number;
    studentViolationCount: number;
  };
}

const EMPTY_CANVAS_GRADE: CanvasGrade = Object.freeze({
  nodeCoverage: { enabled: true, score: null, violations: 0 },
  categoryAccuracy: { enabled: true, score: null, violations: 0 },
  edgeCoverage: { enabled: true, score: null, violations: 0 },
  total: null,
  hasExpertReference: false,
  expertNodeCount: 0,
  matchedNodeCount: 0,
  missingNodeCount: 0,
  miscategorizedCount: 0,
  expertEdgeCount: 0,
  matchedEdgeCount: 0,
  missingEdgeCount: 0,
  extraEdgeCount: 0,
  duplicateStudentCount: 0,
  orphanStudentCount: 0,
  orphanExpertCount: 0,
  parsons: { expertViolationCount: 0, studentViolationCount: 0 },
}) as CanvasGrade;

// Penalty constants — re-use the reading defaults for shape parity.
// These could become user-configurable in a follow-on; for the POC
// they're fixed at the same values as the reader's defaults so the
// recommended/custom toggle's mental model carries over.
const CANVAS_NODE_COVERAGE_PENALTY = 5;
const CANVAS_CATEGORY_PENALTY = 3;
const CANVAS_EDGE_COVERAGE_PENALTY = 5;

/**
 * Stage 6.9: builds a SourceBridge from the reader assessment so
 * canvas grading can match student rects to expert rects across the
 * per-user annotation UUID gap (Stage 3.6 split). Each entry maps a
 * student annotation client_id to the expert highlight it best-matched
 * along with the fractional credit. Empty bridge when no expert
 * reference exists — assessCanvas falls back to identity matching.
 */
export const selectCanvasSourceBridge = createSelector(
  [
    selectViewedHighlights,
    selectCurrentExpertHighlights,
    selectCurrentRubricSettings,
    // Direct gradingView input forces a recompute on enter/exit even
    // if the downstream highlight arrays end up reference-equal (e.g.,
    // owner's annotations array preserved across the toggle). Without
    // this, the bridge memoization can return the prior-call result.
    (state: RootState) => state.workspace.gradingView,
  ],
  (student, expert, settings, _gradingView): SourceBridge => {
    const bySource = new Map<
      string,
      { expertSourceId: string; credit: number; categoriesAgreeAtReader: boolean }
    >();
    if (expert.length === 0 || student.length === 0) return { bySource };
    const thresholdFraction = settings.matchThreshold / 100;
    const assessment = assessHighlights(student, expert, thresholdFraction);
    for (const outcome of assessment.outcomes) {
      if (!outcome.bestMatch) continue;
      const studentId = outcome.bestMatch.student.id;
      const expertId = (outcome.expert as IHighlight).id;
      if (!studentId || !expertId) continue;
      bySource.set(studentId, {
        expertSourceId: expertId,
        credit: outcome.credit,
        categoriesAgreeAtReader: outcome.categoriesAgree ?? false,
      });
    }
    return { bySource };
  }
);

export const selectCanvasGrade = createSelector(
  [
    selectViewedRectangles,
    selectCurrentCanvasExpertSnapshot,
    selectRoleLabels,
    selectCanvasSourceBridge,
  ],
  (studentRects, expertSnapshot, roleLabels, bridge): CanvasGrade => {
    if (!expertSnapshot || expertSnapshot.rectangles.length === 0) {
      return { ...EMPTY_CANVAS_GRADE };
    }
    const expertRects = expertSnapshot.rectangles;
    const result = assessCanvas(studentRects, expertRects, bridge);

    // Stage 6.9: graduated node coverage. Penalty applies to the
    // fractional shortfall (expertNodeCount - totalCoverageCredit)
    // rather than the binary missingNodeCount, so a partial reader
    // match contributes proportionally instead of either 0 or 1.
    const fractionalShortfall =
      expertRects.length - result.totalCoverageCredit;
    const nodeCoverageScore = Math.max(
      0,
      100 - CANVAS_NODE_COVERAGE_PENALTY * fractionalShortfall
    );
    const categoryAccuracyScore = Math.max(
      0,
      100 - CANVAS_CATEGORY_PENALTY * result.miscategorizedCount
    );
    const edgeCoverageScore = Math.max(
      0,
      100 - CANVAS_EDGE_COVERAGE_PENALTY * result.missingEdgeCount
    );

    const total = Math.round(
      (nodeCoverageScore + categoryAccuracyScore + edgeCoverageScore) / 3
    );

    // Parsons parity: run evaluateArgument against both canvases with
    // the same role labels and count diagnostics. Informational only.
    // Wrapping in try/catch since evaluateArgument expects a specific
    // shape — assessCanvas's structural type is wider.
    let expertViolationCount = 0;
    let studentViolationCount = 0;
    try {
      const expertEval = evaluateArgument(expertRects as never[], roleLabels);
      expertViolationCount = expertEval.diagnostics.length;
    } catch (err) {
      console.error("Canvas grade: expert evaluateArgument failed", err);
    }
    try {
      const studentEval = evaluateArgument(studentRects as never[], roleLabels);
      studentViolationCount = studentEval.diagnostics.length;
    } catch (err) {
      console.error("Canvas grade: student evaluateArgument failed", err);
    }

    return {
      nodeCoverage: {
        enabled: true,
        score: Math.round(nodeCoverageScore),
        violations: Math.round(fractionalShortfall),
      },
      categoryAccuracy: {
        enabled: true,
        score: Math.round(categoryAccuracyScore),
        violations: result.miscategorizedCount,
      },
      edgeCoverage: {
        enabled: true,
        score: Math.round(edgeCoverageScore),
        violations: result.missingEdgeCount,
      },
      total,
      hasExpertReference: true,
      expertNodeCount: expertRects.length,
      matchedNodeCount: result.matchedNodeCount,
      missingNodeCount: result.missingNodeCount,
      miscategorizedCount: result.miscategorizedCount,
      expertEdgeCount: result.edgeOutcomes.length,
      matchedEdgeCount: result.matchedEdgeCount,
      missingEdgeCount: result.missingEdgeCount,
      extraEdgeCount: result.extraEdgeCount,
      duplicateStudentCount: result.duplicateStudentCount,
      orphanStudentCount: result.orphanStudentCount,
      orphanExpertCount: result.orphanExpertCount,
      parsons: { expertViolationCount, studentViolationCount },
    };
  }
);

// ============================================================================
// Canvas visual diagnostics (Item 7, Stage 6.5)
//
// Per-student-rect status + ghost expert rects, mirroring
// selectAssessmentDiagnostics for the reader. Drives the canvas
// outline tints in CanvasRectangle and the ghost-rect overlay
// rendered in Canvas.
// ============================================================================

export type CanvasRectDiagnostic =
  | "matched"
  | "category_mismatch"
  | "low_overlap"
  | "extra"
  | "orphan";

import type { Rectangle as SliceRectangle } from "../../types/workspace";

export interface CanvasDiagnostics {
  /** Status per student rectangle, indexed by array position. */
  rectStatusByIdx: CanvasRectDiagnostic[];
  /**
   * Per-rect bullet messages surfaced by the CanvasDiagnosticLayer's
   * hover tooltip. Index-aligned with the rects array; empty array
   * for matched rects (no badge rendered).
   */
  messagesByIdx: string[][];
  /**
   * Expert rectangles with no student match — rendered as faded
   * dashed-outline ghosts at their expert positions so the grader
   * (or contributor seeing their own grade) can see what was missed.
   */
  ghostExpertRects: SliceRectangle[];
  /** True iff an expert canvas snapshot exists. */
  active: boolean;
}

const EMPTY_CANVAS_DIAGNOSTICS: CanvasDiagnostics = Object.freeze({
  rectStatusByIdx: [],
  messagesByIdx: [],
  ghostExpertRects: [],
  active: false,
}) as CanvasDiagnostics;

export const selectCanvasDiagnostics = createSelector(
  [
    selectViewedRectangles,
    selectCurrentCanvasExpertSnapshot,
    selectCanvasSourceBridge,
    (state: RootState) => state.workspace.gradingView,
  ],
  (studentRects, expertSnapshot, bridge, _gradingView): CanvasDiagnostics => {
    if (!expertSnapshot || expertSnapshot.rectangles.length === 0) {
      return EMPTY_CANVAS_DIAGNOSTICS;
    }
    const expertRects = expertSnapshot.rectangles;
    const result = assessCanvas(studentRects, expertRects, bridge);

    const rectStatusByIdx: CanvasRectDiagnostic[] = new Array(
      studentRects.length
    ).fill("extra");
    // bestStudentIdx → expert outcome reference, populated below so
    // the messages pass can read the expert rect's category / credit.
    const outcomeByStudentIdx = new Map<number, (typeof result.nodeOutcomes)[number]>();

    studentRects.forEach((r, i) => {
      if (!r.source_annotation_client_id) {
        rectStatusByIdx[i] = "orphan";
      }
    });

    result.nodeOutcomes.forEach((o) => {
      if (o.bestStudentIdx == null) return;
      outcomeByStudentIdx.set(o.bestStudentIdx, o);
      if (o.credit === 1) {
        rectStatusByIdx[o.bestStudentIdx] = o.categoriesAgree
          ? "matched"
          : "category_mismatch";
      } else if (o.credit > 0) {
        rectStatusByIdx[o.bestStudentIdx] = "low_overlap";
      }
    });

    const messagesByIdx: string[][] = rectStatusByIdx.map((status, i) => {
      const rect = studentRects[i];
      switch (status) {
        case "category_mismatch": {
          const outcome = outcomeByStudentIdx.get(i);
          const expertCat = outcome?.expert.category;
          const studentCat = rect?.category;
          return [
            "Right node, wrong category.",
            `Expected: ${expertCat ? `"${expertCat}"` : "(uncategorized)"}`,
            `Got: ${studentCat ? `"${studentCat}"` : "(uncategorized)"}`,
          ];
        }
        case "low_overlap": {
          const outcome = outcomeByStudentIdx.get(i);
          const creditPct = Math.round((outcome?.credit ?? 0) * 100);
          return [
            "Underlying annotation overlaps an expert highlight only partially.",
            `Bridge credit: ${creditPct}%.`,
          ];
        }
        case "extra":
          return [
            "No matching expert rectangle.",
            "May be informational — beyond the rubric's expert set.",
          ];
        case "orphan":
          return [
            "Rectangle has no source annotation.",
            "Not assessable against the expert canvas.",
          ];
        default:
          return [];
      }
    });

    // Ghosts: expert rects with no paired student (credit 0). Partials
    // (0 < credit < 1) already surface via the student rect's badge;
    // adding a duplicate ghost at the expert position would be noisy.
    const ghostExpertRects = result.nodeOutcomes
      .filter((o) => o.bestStudent === null)
      .map((o) => o.expert as SliceRectangle);

    return {
      rectStatusByIdx,
      messagesByIdx,
      ghostExpertRects,
      active: true,
    };
  }
);

/**
 * Get canvas name from the current canvas.
 * Memoized - only recomputes when current canvas changes.
 */
export const selectCurrentCanvasName = createSelector(
  [selectCurrentCanvas],
  (canvas) => canvas?.canvas_name || ''
);

/**
 * Get editor state from the current paper.
 * Memoized - only recomputes when current paper changes.
 */
export const selectCurrentPaperState = createSelector(
  [selectCurrentPaper],
  (paper) => paper?.paper_data?.editorState || null
);

// ============================================================================
// Resource metadata selectors
// ============================================================================

/**
 * Get the name of the current reading.
 * Memoized - only recomputes when current reading changes.
 */
export const selectCurrentReadingName = createSelector(
  [selectCurrentReading],
  (reading) => reading?.reading_name || ''
);

/**
 * Get the name of the current paper.
 * Memoized - only recomputes when current paper changes.
 */
export const selectCurrentPaperName = createSelector(
  [selectCurrentPaper],
  (paper) => paper?.paper_name || ''
);

/**
 * Check if current reading has data loaded.
 * Memoized - only recomputes when current reading changes.
 */
export const selectHasReadingData = createSelector(
  [selectCurrentReading],
  (reading) => !!reading?.reading_data
);

/**
 * Check if current canvas has data loaded.
 * Memoized - only recomputes when current canvas changes.
 */
export const selectHasCanvasData = createSelector(
  [selectCurrentCanvas],
  (canvas) => !!canvas?.canvas_data
);

/**
 * Check if current paper has data loaded.
 * Memoized - only recomputes when current paper changes.
 */
export const selectHasPaperData = createSelector(
  [selectCurrentPaper],
  (paper) => !!paper?.paper_data
);

// ============================================================================
// Convenience selectors for ResourceSelector component
// ============================================================================

/**
 * Get all reading options for dropdown (id and name pairs).
 * Memoized - only recomputes when readings array changes.
 */
export const selectReadingOptions = createSelector(
  [selectReadings],
  (readings) => readings.map(r => ({ id: r.reading_id, name: r.reading_name }))
);

/**
 * Get all canvas options for dropdown (id and name pairs).
 * Memoized - only recomputes when canvases array changes.
 */
export const selectCanvasOptions = createSelector(
  [selectCanvases],
  (canvases) => canvases.map(c => ({ id: c.canvas_id, name: c.canvas_name }))
);

/**
 * Get all paper options for dropdown (id and name pairs).
 * Memoized - only recomputes when papers array changes.
 */
export const selectPaperOptions = createSelector(
  [selectPapers],
  (papers) => papers.map(p => ({ id: p.paper_id, name: p.paper_name }))
);
